/* ===================================================================
   Google Business Profile post queue hooks (2026-08-05).

   - useGbpPosts()        GET  /api/v1/gbp/posts   (the queue)
   - useGbpPostMutations() compose / edit / delete / publish now

   Unlike the social lane (which is dry-run until Meta App Review), these
   posts DO publish: GBP API access is approved and needs no new scope.
   Read hooks stay disabled until a Bearer rr_ token exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type GbpImageRequirements,
  type GbpImageUploadOpts,
  type GbpImageUploadResult,
  type GbpPost,
  type GbpPostInput,
  type GbpPostPatch,
  type GbpPostResult,
  type GbpPostStatus,
  type GbpPostsResponse,
  type GbpPublishNowResult,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useGbpPosts(
  params: { status?: GbpPostStatus } = {},
): UseQueryResult<GbpPostsResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.gbpPosts(params.status ? { status: params.status } : undefined),
    queryFn: ({ signal }) => api.listPosts(params, signal),
    enabled: hasToken,
    staleTime: 15_000,
  });
}

export function useGbpPostMutations() {
  const qc = useQueryClient();
  /* Every mutation invalidates the whole queue rather than patching one row:
     publishing changes status, posted_at, external_post_url and can also fail,
     so a refetch is the honest way to show what actually happened. */
  const invalidate = () => qc.invalidateQueries({ queryKey: ['gbp', 'posts'] });

  const create = useMutation<GbpPostResult, Error, GbpPostInput>({
    mutationFn: (input) => api.createPost(input),
    onSuccess: invalidate,
  });

  const update = useMutation<GbpPostResult, Error, { id: string; patch: GbpPostPatch }>({
    mutationFn: ({ id, patch }) => api.updatePost(id, patch),
    onSuccess: invalidate,
  });

  const remove = useMutation<{ client_id: string; deleted: string }, Error, string>({
    mutationFn: (id) => api.deletePost(id),
    onSuccess: invalidate,
  });

  const publishNow = useMutation<GbpPublishNowResult, Error, string>({
    mutationFn: (id) => api.publishNow(id),
    onSuccess: invalidate,
  });

  return { create, update, remove, publishNow };
}

/* Google's photo rules (formats + max size + caps), so the composer can state
   the limits BEFORE a pick rather than after a rejected upload. Cached long --
   these change about never. Disabled until a token exists, like the queue. */
export function useGbpImageRequirements(): UseQueryResult<
  GbpImageRequirements,
  Error
> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.gbpImageRequirements(),
    queryFn: ({ signal }) => api.getImageRequirements(signal),
    enabled: hasToken,
    staleTime: 60 * 60 * 1000,
  });
}

/* Upload + delete for the one image a composer attaches to a post. The upload
   returns the stored image (its public `url` is what rides in
   design.photo_urls). Delete is used only to clean up an image the owner picked
   and then removed BEFORE creating the post -- once a post references the URL,
   the post owns it and we never delete the source out from under it. */
export function useGbpImageMutations() {
  const upload = useMutation<
    GbpImageUploadResult,
    Error,
    { dataUrl: string; opts?: GbpImageUploadOpts }
  >({
    mutationFn: ({ dataUrl, opts }) => api.uploadImage(dataUrl, opts),
  });

  const remove = useMutation<
    { client_id: string; deleted: string },
    Error,
    string
  >({
    mutationFn: (id) => api.deleteImage(id),
  });

  return { upload, remove };
}

/* Queue ordering for display: anything still to go out, soonest first; then
   drafts; then history, most recent first. The owner cares about "what's
   coming" far more than "what already went". */
export function sortGbpPosts(posts: GbpPost[]): GbpPost[] {
  const rank = (p: GbpPost) =>
    p.status === 'scheduled' || p.status === 'publishing' ? 0
      : p.status === 'draft' ? 1
        : 2;
  return [...posts].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 0) {
      return (a.scheduled_at || '').localeCompare(b.scheduled_at || '');
    }
    return (b.updated_at || b.created_at || '').localeCompare(
      a.updated_at || a.created_at || '',
    );
  });
}
